<template>
    <div>
        <table
            class="min-w-full border border-gray-300 divide-y divide-gray-300 dark:border-neutral-800 dark:divide-neutral-800"
        >
            <thead class="bg-gray-50 dark:bg-neutral-800">
                <tr>
                    <th
                        scope="col"
                        class="py-3.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-neutral-50"
                    >
                        {{ lang('products::attributes.fields.name') }}
                    </th>
                    <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 dark:text-neutral-50"
                    >
                        {{ lang('products::values.fields.value') }}
                    </th>
                    <th
                        v-tooltip="lang('products::values.fields.price_rule_tooltip')"
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 whitespace-nowrap dark:text-neutral-50"
                    >
                        {{ lang('products::values.fields.price_rule') }}
                    </th>
                    <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 whitespace-nowrap dark:text-neutral-50"
                    >
                        {{ lang('products::values.fields.is_default') }}
                    </th>
                    <th
                        scope="col"
                        class="py-3.5 px-3 text-left text-xs font-semibold text-gray-900 whitespace-nowrap dark:text-neutral-50"
                    >
                        {{ lang('products::attributes.fields.is_selectable') }}
                    </th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6"></th>
                </tr>
            </thead>
            <tbody v-if="hasAttributes" class="divide-y divide-gray-200 dark:divide-neutral-800">
                <template v-for="(group, groupIndex) in groupedAttributes" :key="groupIndex">
                    <template v-for="(attribute, index) in group.attributes" :key="attribute.id || index">
                        <tr
                            :id="index === 0 ? `attribute-${group.id}` : ''"
                            :class="{
                                'bg-green-100 dark:bg-green-900/20': attribute.is_default,
                            }"
                        >
                            <th
                                v-if="index === 0"
                                scope="row"
                                class="w-[20%] py-3.5 px-4 text-left text-sm font-semibold text-gray-900 bg-gray-50 dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 align-top"
                                :rowspan="
                                    group.attributes.length +
                                    (containsSelectableAttributes(group) && !isConfiguration ? 1 : 0)
                                "
                            >
                                <AdminSelect
                                    v-if="!isConfiguration"
                                    v-model="group.id"
                                    :searchable="true"
                                    :options="attributes"
                                    @change="changeGroupId(group, $event)"
                                >
                                    <template #optionsAfter>
                                        <div
                                            class="py-3 text-center border-t border-gray-200 cursor-pointer dark:border-neutral-700 hover:bg-green-50 dark:hover:bg-green-950"
                                            @click="openCreateAttributeModal"
                                        >
                                            <span class="inline-flex items-center gap-2">
                                                <span>
                                                    {{ lang('products::attributes.actions.create') }}
                                                </span>
                                                <icon icon="hero/outline/plus" class="text-green-700 size-3"></icon>
                                            </span>
                                        </div>
                                    </template>
                                </AdminSelect>
                                <div v-else>
                                    {{ group.name }}
                                </div>
                            </th>
                            <td
                                class="w-auto py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap"
                                :class="[
                                    {
                                        'align-top': isExpanded(attribute),
                                    },
                                ]"
                            >
                                <div v-if="!isConfiguration">
                                    <AdminSuggestions
                                        v-model="attribute.value"
                                        ajax-route="products/attributes.admin.api.suggestions"
                                        :params="{ attribute_id: attribute.attribute_id }"
                                        :searchable="true"
                                    />
                                    <div v-show="isExpanded(attribute)">
                                        <div
                                            v-if="attribute.is_selectable || attribute.is_value_selectable"
                                            class="mt-2 space-y-2"
                                        >
                                            <AdminTextarea
                                                v-model="attribute.description"
                                                :placeholder="lang('products::values.fields.description')"
                                            ></AdminTextarea>
                                            <AdminFile
                                                v-model="attribute.media"
                                                :accept="'image/*'"
                                                :recommended="{
                                                    width: 1920,
                                                    height: 1080,
                                                    aspect: '16:9',
                                                    sizes: ['1920×1080', '1600×900', '1280×720'],
                                                }"
                                            ></AdminFile>
                                        </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <AdminText v-model="attribute.value" readonly />
                                    <div v-show="isExpanded(attribute)">
                                        <div
                                            v-if="attribute.is_selectable || attribute.is_value_selectable"
                                            class="mt-2 space-y-2"
                                        >
                                            <AdminTextarea
                                                v-model="attribute.description"
                                                :placeholder="lang('products::values.fields.description')"
                                            ></AdminTextarea>
                                            <AdminFile v-model="attribute.media"></AdminFile>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 align-top whitespace-nowrap w-[120px]"
                            >
                                <div
                                    v-if="attribute.is_selectable || attribute.is_value_selectable"
                                    class="flex items-center justify-between w-full gap-4"
                                >
                                    <AdminSelect
                                        v-model="attribute.modifier_type"
                                        size="sm"
                                        :options="modifierTypes"
                                    ></AdminSelect>
                                    <AdminText
                                        v-if="attribute.modifier_type !== null"
                                        v-model="attribute.modifier_value"
                                        size="xs"
                                        @blur="formatModifierValue(attribute)"
                                    ></AdminText>
                                </div>
                            </td>
                            <td
                                class="px-3 pt-5 text-sm font-medium text-center text-gray-900 align-top whitespace-nowrap w-[80px]"
                            >
                                <label v-if="attribute.is_value_selectable" class="cursor-pointer select-none">
                                    <AdminRadio
                                        v-model="attribute.is_default"
                                        :value="true"
                                        @change="setDefault(attribute, group)"
                                    />
                                </label>
                            </td>
                            <td
                                class="px-3 pt-5 text-sm font-medium text-center text-gray-900 align-top whitespace-nowrap w-[80px]"
                            >
                                <AdminCheckbox
                                    v-model="attribute.is_value_selectable"
                                    :checked="attribute.is_value_selectable"
                                ></AdminCheckbox>
                            </td>
                            <td class="w-18 py-3.5 pr-4 sm:pr-6 align-top">
                                <div class="flex gap-1">
                                    <Button :disabled="isConfiguration" @click.prevent="removeAttribute(attribute)">
                                        <span class="flex items-center justify-center size-8">
                                            <icon icon="hero/outline/trash" class="size-4"></icon>
                                        </span>
                                    </Button>
                                    <Button
                                        v-if="attribute.is_value_selectable || attribute.is_selectable"
                                        :class="{
                                            'bg-gray-100 dark:bg-neutral-900': isExpanded(attribute),
                                        }"
                                        @click.prevent="toggleAttribute(attribute)"
                                    >
                                        <span class="flex items-center justify-center size-8">
                                            <icon icon="hero/outline/ellipsis-horizontal" class="size-4"></icon>
                                        </span>
                                    </Button>
                                </div>
                            </td>
                        </tr>
                        <tr
                            v-if="
                                containsSelectableAttributes(group) &&
                                !isConfiguration &&
                                index === group.attributes.length - 1
                            "
                        >
                            <td colspan="6" class="px-4 py-3.5 bg-gray-50 dark:bg-neutral-900 text-center">
                                <Button
                                    v-if="containsSelectableAttributes(group) && !isConfiguration"
                                    @click.prevent="addNewValueToAttribute(attribute.attribute_id)"
                                >
                                    <span class="flex items-center justify-center size-8">
                                        <icon icon="hero/outline/plus" class="size-4"></icon>

                                        <span class="sr-only">
                                            {{ lang('products::values.actions.add_value') }}
                                        </span>
                                    </span>
                                </Button>
                            </td>
                        </tr>
                    </template>
                </template>
            </tbody>
            <tbody v-else>
                <tr>
                    <td
                        colspan="6"
                        class="py-3.5 space-y-1 text-center text-sm font-semibold text-neutral-900 dark:text-neutral-100"
                    >
                        <p>
                            {{ lang('products::values.messages.no_values') }}
                        </p>
                        <p>
                            <Button v-if="!isConfiguration" @click.prevent="addAttribute">
                                <span>
                                    {{ lang('products::values.actions.add_attribute') }}
                                </span>
                                <icon icon="hero/outline/plus" class="size-4"></icon>
                            </Button>
                        </p>
                    </td>
                </tr>
            </tbody>
            <tfoot class="bg-gray-50 dark:bg-neutral-900">
                <tr>
                    <th
                        colspan="5"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 sm:pl-6"
                    ></th>
                    <th class="w-12 py-3.5 pr-4 sm:pr-6 text-right">
                        <Button v-if="!isConfiguration" @click.prevent="addAttribute">
                            <span class="flex items-center justify-center size-8">
                                <icon icon="hero/outline/plus" class="size-4"></icon>
                            </span>
                        </Button>
                    </th>
                </tr>
            </tfoot>
        </table>

        <Modal :show="showCreateAttributeModal" @close="closeCreateAttributeModal">
            <div class="flex flex-col -m-6 space-y-4">
                <div class="flex flex-col">
                    <AttributesForm
                        prefix="products/attributes"
                        :redirect="
                            $page.props.object && $page.props.object.id
                                ? route('products.admin.edit', { id: $page.props.object.id, tab: 'attributes' })
                                : null
                        "
                    />
                </div>
            </div>
        </Modal>

        <MountedTeleport to="#aside">
            <Sortable :list="groupedAttributes" item-key="id" @end="sorted">
                <template #item="{ element }">
                    <div
                        :key="element.id"
                        class="flex items-stretch text-xs border select-none bg-gray-50 hover:bg-blue-100 border-neutral-200 dark:bg-neutral-900 dark:hover:bg-blue-950 dark:border-neutral-800"
                    >
                        <a :href="`#attribute-${element.id}`" class="flex items-center justify-center w-6 grow-0">
                            <icon icon="hero/outline/chevron-left" class="size-4"></icon>
                        </a>
                        <div class="flex-auto px-4 py-2">
                            <a :href="`#attribute-${element.attributes[0].attribute_id}`">
                                {{ element.attributes[0].name }}
                            </a>
                        </div>
                        <div
                            class="flex items-center w-6 text-center bg-gray-100 border grow-0 dark:border-neutral-800 dark:bg-neutral-900 cursor-grab"
                        >
                            <icon icon="hero/outline/dots-vertical" class="mx-auto pointer-events-none size-4"></icon>
                        </div>
                    </div>
                </template>
            </Sortable>

            <div v-if="isConfiguration" class="p-4">
                <div
                    class="px-4 py-2 space-y-2 text-xs text-teal-600 bg-teal-100 border border-teal-300 rounded dark:bg-teal-800 dark:border-teal-700 dark:text-teal-400"
                >
                    <div>
                        {{ lang('products::products.wizard.types.configuration_attributes_message') }}
                    </div>
                    <div v-if="$page.props.object.parent" class="flex justify-center">
                        <Button
                            :a="true"
                            target="_blank"
                            :href="
                                route('products.admin.edit', { id: $page.props.object.parent.id, tab: 'attributes' })
                            "
                        >
                            <span class="flex items-center gap-2">
                                <span>
                                    {{ $page.props.object.parent.name }}
                                </span>
                                <Icon icon="hero/outline/arrow-top-right-on-square" class="size-3"></Icon>
                            </span>
                        </Button>
                    </div>
                </div>
            </div>
        </MountedTeleport>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { lang } from '@/lang/composable';
import AttributesForm from '@src/Products/Pages/Attributes/Form.vue';
import MountedTeleport from '@src/Common/Components/MountedTeleport.vue';
import { usePage } from '@inertiajs/vue3';
import type { FieldComponent } from '@src/Common/Components/types';

const modelValue = defineModel({
    type: [Object, undefined],
    default: () => [],
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<FieldComponent>(), {});

const attributes = ref([]);

const groupedAttributes = ref([]);

const $page = usePage();

const updateGroupedAttributes = () => {
    const groupsMap = new Map();

    modelValue.value
        .sort((a, b) => a.order - b.order)
        .forEach((attribute) => {
            const attributeId = attribute.attribute_id;
            if (!groupsMap.has(attributeId)) {
                groupsMap.set(attributeId, {
                    id: attributeId,
                    name: attribute.name,
                    attributes: [],
                });
            }
            groupsMap.get(attributeId).attributes.push(attribute);
        });

    // Aktualizujeme groupedAttributes.value "na místě"
    const newGroupedAttributes = Array.from(groupsMap.values());
    groupedAttributes.value.splice(0, groupedAttributes.value.length, ...newGroupedAttributes);
};

const hasAttributes = computed(() => {
    return modelValue.value && modelValue.value.length > 0;
});

const isConfiguration = computed(() => $page.props.object?.type === 'configuration');

const addNewValueToAttribute = (attribute_id) => {
    modelValue.value.push({
        id: null,
        attribute_id: attribute_id,
        value: '',
        is_selectable: true,
        is_default: false,
        is_value_selectable: false,
        modifier_type: null,
        modifier_value: 0.0,
        description: '',
        media: null,
    });
};

const addAttribute = () => {
    modelValue.value.push({
        id: null,
        attribute_id: null,
        value: '',
        is_selectable: false,
        is_default: false,
        is_value_selectable: false,
        modifier_type: null,
        modifier_value: 0.0,
        description: '',
        media: null,
    });
};

const removeAttribute = (attribute) => {
    modelValue.value = modelValue.value.filter((item) => item !== attribute);
};

const loadAttributes = () => {
    axios.get(route('products/attributes.admin.api.select')).then(({ data }) => {
        attributes.value = data;
    });
};

const modifierTypes = [
    {
        id: null,
        name: lang('products::values.fields.no_modifier'),
    },
    {
        id: 'add',
        name: '+',
    },
    {
        id: 'subtract',
        name: '-',
    },
    {
        id: 'multiply',
        name: '*',
    },
    {
        id: 'divide',
        name: '/',
    },
];

const sorted = (event) => {
    // Přesuneme skupinu na nové místo
    const movedGroup = groupedAttributes.value.splice(event.oldIndex, 1)[0];
    groupedAttributes.value.splice(event.newIndex, 0, movedGroup);

    // Aktualizujeme 'order' pro všechny atributy ve skupinách
    groupedAttributes.value.forEach((group, groupIndex) => {
        const newOrder = groupIndex;
        group.attributes.forEach((attribute) => {
            attribute.order = newOrder;
        });
    });

    // Aktualizujeme modelValue
    modelValue.value = groupedAttributes.value.flatMap((group) => group.attributes);

    // Zavoláme updateGroupedAttributes pro obnovení skupin
    updateGroupedAttributes();
};

const changeGroupId = (group, newId) => {
    group.attributes.forEach((attribute) => {
        attribute.attribute_id = newId;
    });
};

/**
 * --------------------
 * Modal
 * --------------------
 */
const showCreateAttributeModal = ref(false);

const openCreateAttributeModal = () => {
    showCreateAttributeModal.value = true;
};

const closeCreateAttributeModal = () => {
    showCreateAttributeModal.value = false;
};

const setDefault = (attribute, group) => {
    group.attributes.forEach((item) => {
        item.is_default = item === attribute;
    });
};

watch(modelValue, updateGroupedAttributes, { immediate: true, deep: true });

const containsSelectableAttributes = (group) =>
    group.attributes.some((attribute) => attribute.is_selectable || attribute.is_value_selectable);

/**
 * --------------------
 * Expansible attributes
 * --------------------
 */
const expandedAttribute = ref(null);

const expandAttribute = (attribute) => {
    expandedAttribute.value = attribute;
};

const closeAttribute = () => {
    expandedAttribute.value = null;
};

const toggleAttribute = (attribute) => {
    if (isExpanded(attribute)) {
        closeAttribute();
    } else {
        expandAttribute(attribute);
    }
};

const isExpanded = (attribute) => {
    return expandedAttribute.value === attribute;
};

const formatModifierValue = (attribute) => {
    if (!isNaN(attribute.modifier_value)) {
        attribute.modifier_value = parseFloat(attribute.modifier_value);
    }
};

const formatAttributeModifierValues = () => {
    modelValue.value.forEach((attribute) => {
        if (attribute.modifier_value != null && !isNaN(attribute.modifier_value)) {
            attribute.modifier_value = parseFloat(attribute.modifier_value);
        }
    });
};

onMounted(() => {
    loadAttributes();

    formatAttributeModifierValues();
});
</script>
